import type { ReactNode } from 'react';
import { StatusLamp } from './StatusLamp.tsx';
import type { LampTone } from './StatusLamp.tsx';
import { cx } from './cx.ts';

/**
 * KPI stat card: caption label row (optionally dot-lit), mono `stat`
 * value (the ONLY sanctioned 600 weight, DESIGN.md §3), mono delta
 * line. `tone` recolors the value for alert states; the card frame
 * warms to `bad-line` when tone is `bad`.
 */
export type KpiTone = 'default' | 'ok' | 'warn' | 'bad';

const VALUE_TONE: Record<KpiTone, string> = {
  default: 'text-ink',
  ok: 'text-moss',
  warn: 'text-warn',
  bad: 'text-bad',
};

export type KpiDeltaDirection = 'up' | 'down' | 'flat';

const DELTA_TONE: Record<'good' | 'bad' | 'neutral', string> = {
  good: 'text-moss',
  bad: 'text-bad',
  neutral: 'text-ink-low',
};

interface Props {
  readonly label: ReactNode;
  readonly value: ReactNode;
  readonly unit?: ReactNode;
  readonly delta?: {
    readonly text: ReactNode;
    readonly tone: 'good' | 'bad' | 'neutral';
  };
  readonly tone?: KpiTone;
  readonly lamp?: LampTone;
  readonly pulse?: boolean;
  readonly className?: string;
  readonly testId?: string;
}

export function KpiCard({
  label,
  value,
  unit,
  delta,
  tone = 'default',
  lamp,
  pulse = false,
  className,
  testId,
}: Props): JSX.Element {
  return (
    <div
      data-testid={testId}
      className={cx(
        'rounded-lg border bg-card p-5',
        tone === 'bad' ? 'border-bad-line bg-bad-tint/30' : 'border-line',
        className,
      )}
    >
      <div className="flex items-center gap-2 text-caption text-ink-low">
        {lamp !== undefined ? <StatusLamp tone={lamp} size="sm" pulse={pulse} /> : null}
        {label}
      </div>
      <div className={cx('mt-2 font-mono text-stat tabular-nums', VALUE_TONE[tone])}>
        {value}
        {unit !== undefined && unit !== null ? (
          <span className="ml-1 text-body font-normal text-ink-low">{unit}</span>
        ) : null}
      </div>
      {delta !== undefined ? (
        <div className={cx('mt-1.5 font-mono text-label tabular-nums', DELTA_TONE[delta.tone])}>
          {delta.text}
        </div>
      ) : null}
    </div>
  );
}
